import {
  RaidPlanActor,
  RaidPlanAssignmentEntity,
  RaidPlanCast,
  RaidPlanEncounterAssignmentAbility,
} from 'app/frontend/types/custom/RaidPlan'
import { EncounterAbilityRow } from '../../TeamPlans/hooks/useTeamPlanComputedData'
import {
  AssignmentViewMode,
  getEffectiveAbilityCustomGrouping,
  getEffectiveAssignmentGroups,
  getEffectiveAssignmentViewMode,
  getAssignmentScopeIndexes,
  normalizeAssignmentBox,
  normalizeAssignmentCast,
} from '../../TeamPlans/components/assignmentTimeline'
import { getAllSpecializations } from 'app/frontend/base/services/characterValues'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import { resolveEventParticipantCharacter } from './eventActivityParticipants'
import {
  EncounterAssignmentEntityOverride,
  EventPlanCastTargetOverrides,
  getCastTargetEntityOverride,
} from './eventActivityPlanOverrides'

type RoleKey = 'tank' | 'healer' | 'dps' | 'ranged' | 'melee' | 'anyone'

export type ResolvedAssignmentParticipant = {
  participantId: number
  characterId: number
  classId?: number
  role: RoleKey
  specId: number | null
  name: string
  characterClass: wowaudit.CharacterReference['characterClass']
}

export type ResolvedAssignmentEntity =
  | {
      kind: 'participant'
      participant: ResolvedAssignmentParticipant
      sourceEntity: RaidPlanAssignmentEntity
      issue?: EventActivityTimelineResolverIssue
      isOverridden?: boolean
    }
  | {
      kind: 'aggregate'
      sourceEntity: RaidPlanAssignmentEntity
      issue?: EventActivityTimelineResolverIssue
      isOverridden?: boolean
    }
  | {
      kind: 'plain'
      sourceEntity: RaidPlanAssignmentEntity
      issue?: EventActivityTimelineResolverIssue
      isOverridden?: boolean
    }

export type EventActivityTimelineResolverIssue = {
  type: 'insufficient_candidates' | 'unmatched_member'
  spellId: number
  scopeId: string
  role?: RoleKey
  requested: number
  available: number
  missing: number
  memberName?: string
}

type ResolveEventActivityTimelineAssignmentsInput = {
  activityId: number
  entries: EventActivityParticipantEntry[]
  encounterRows: EncounterAbilityRow[]
  abilities: RaidPlanEncounterAssignmentAbility[]
}

type ResolveEventActivityTimelineActorsInput = {
  activityId: number
  entries: EventActivityParticipantEntry[]
  actors: RaidPlanActor[]
}

export type ResolvedTimelineActor = {
  actor: RaidPlanActor
  participant?: ResolvedAssignmentParticipant
  issue?: EventActivityTimelineActorResolverIssue
  isOverridden?: boolean
  isIgnored?: boolean
}

export type EventActivityTimelineActorResolverIssue = {
  type: 'unmatched_actor'
  actorLabel: string
  requested: number
  available: number
  missing: number
}

const unavailableStatuses: wowaudit.EventParticipantReference['status'][] = [
  'absent',
  'missing',
  'removed',
]

const asResolverRole = (role: string): RoleKey => {
  if (role === 'tank') return 'tank'
  if (role === 'heal' || role === 'healer') return 'healer'
  if (role === 'melee') return 'melee'
  if (role === 'ranged') return 'ranged'
  if (role === 'dps') return 'dps'
  if (role === 'anyone' || role === 'any_player') return 'anyone'
  return 'anyone'
}

const matchesRole = (
  participantRole: RoleKey,
  requestedRole: RoleKey,
): boolean => {
  if (requestedRole === 'anyone') return true
  if (requestedRole === 'dps') {
    return participantRole === 'melee' || participantRole === 'ranged'
  }
  return participantRole === requestedRole
}

export const toResolvedParticipants = (
  entries: EventActivityParticipantEntry[],
  activityId: number,
) => {
  return entries
    .filter((entry) => !unavailableStatuses.includes(entry.participant.status))
    .filter((entry) => entry.selections[activityId]?.selected)
    .map((entry) => {
      const selection = entry.selections[activityId]
      const character = resolveEventParticipantCharacter(entry, activityId)
      const isMainSelection =
        selection.characterId === entry.characterId &&
        selection.role === entry.role &&
        selection.specId === entry.specId
      return {
        participant: {
          participantId: entry.participant.id,
          characterId: character.id,
          classId: character.classId,
          role: asResolverRole(selection.role),
          specId: selection.specId,
          name: character.name,
          characterClass: character.characterClass,
        },
        isMainSelection,
      }
    })
    .sort((left, right) => {
      if (left.isMainSelection !== right.isMainSelection) {
        return Number(right.isMainSelection) - Number(left.isMainSelection)
      }
      return left.participant.name.localeCompare(right.participant.name)
    })
    .map((entry) => entry.participant)
}

const pickFirstUnusedParticipant = (
  participants: ResolvedAssignmentParticipant[],
  usedParticipantIds: Set<number>,
  matches: (participant: ResolvedAssignmentParticipant) => boolean,
) => {
  return participants.find(
    (participant) =>
      !usedParticipantIds.has(participant.participantId) &&
      matches(participant),
  )
}

export const resolveEventActivityTimelineActors = ({
  activityId,
  entries,
  actors,
}: ResolveEventActivityTimelineActorsInput) => {
  const participants = toResolvedParticipants(entries, activityId)
  const roleBySpecId = Object.entries(getAllSpecializations()).reduce<
    Record<number, RoleKey>
  >((acc, [specId, specialization]) => {
    acc[Number(specId)] = asResolverRole(specialization.default_role)
    return acc
  }, {})
  const usedParticipantIds = new Set<number>()
  const resolvedActors: ResolvedTimelineActor[] = actors.map((actor) => ({
    actor,
  }))

  resolvedActors.forEach((resolvedActor) => {
    const preferredCharacterId = resolvedActor.actor.preferred_character_id
    if (typeof preferredCharacterId !== 'number') return
    const preferredParticipant = pickFirstUnusedParticipant(
      participants,
      usedParticipantIds,
      (participant) => participant.characterId === preferredCharacterId,
    )
    if (!preferredParticipant) return
    resolvedActor.participant = preferredParticipant
    usedParticipantIds.add(preferredParticipant.participantId)
  })

  resolvedActors.forEach((resolvedActor) => {
    if (resolvedActor.participant) return
    if (resolvedActor.actor.spec_id === null) return
    const specParticipant = pickFirstUnusedParticipant(
      participants,
      usedParticipantIds,
      (participant) => participant.specId === resolvedActor.actor.spec_id,
    )
    if (!specParticipant) return
    resolvedActor.participant = specParticipant
    usedParticipantIds.add(specParticipant.participantId)
  })

  resolvedActors.forEach((resolvedActor) => {
    if (resolvedActor.participant) return
    if (resolvedActor.actor.spec_id === null) return
    if (resolvedActor.actor.casts.length === 0) return
    const actorRole = roleBySpecId[resolvedActor.actor.spec_id]
    if (!actorRole) return
    const classRoleParticipant = pickFirstUnusedParticipant(
      participants,
      usedParticipantIds,
      (participant) =>
        participant.classId === resolvedActor.actor.class_id &&
        participant.specId === null &&
        matchesRole(participant.role, actorRole),
    )
    if (!classRoleParticipant) return
    resolvedActor.participant = classRoleParticipant
    usedParticipantIds.add(classRoleParticipant.participantId)
  })

  resolvedActors.forEach((resolvedActor) => {
    if (resolvedActor.participant) return
    if (resolvedActor.actor.spec_id !== null) return
    const classParticipant = pickFirstUnusedParticipant(
      participants,
      usedParticipantIds,
      (participant) => participant.classId === resolvedActor.actor.class_id,
    )
    if (!classParticipant) return
    resolvedActor.participant = classParticipant
    usedParticipantIds.add(classParticipant.participantId)
  })

  const issues = resolvedActors
    .filter((resolvedActor) => !resolvedActor.participant)
    .map((resolvedActor) => {
      const available =
        resolvedActor.actor.spec_id !== null
          ? participants.filter(
              (participant) =>
                !usedParticipantIds.has(participant.participantId) &&
                participant.specId === resolvedActor.actor.spec_id,
            ).length
          : participants.filter(
              (participant) =>
                !usedParticipantIds.has(participant.participantId) &&
                participant.classId === resolvedActor.actor.class_id,
            ).length
      const issue: EventActivityTimelineActorResolverIssue = {
        type: 'unmatched_actor',
        actorLabel: resolvedActor.actor.label,
        requested: 1,
        available,
        missing: 1,
      }
      resolvedActor.issue = issue
      return issue
    })

  return {
    resolvedActors,
    issues,
  }
}

const createRoundRobinPicker = (
  participants: ResolvedAssignmentParticipant[],
  rolePriority: RoleKey[],
) => {
  const participantIdsByRole: Record<RoleKey, number[]> = {
    tank: participants
      .filter((participant) => matchesRole(participant.role, 'tank'))
      .map((participant) => participant.participantId),
    healer: participants
      .filter((participant) => matchesRole(participant.role, 'healer'))
      .map((participant) => participant.participantId),
    melee: participants
      .filter((participant) => matchesRole(participant.role, 'melee'))
      .map((participant) => participant.participantId),
    ranged: participants
      .filter((participant) => matchesRole(participant.role, 'ranged'))
      .map((participant) => participant.participantId),
    dps: participants
      .filter((participant) => matchesRole(participant.role, 'dps'))
      .map((participant) => participant.participantId),
    anyone: participants.map((participant) => participant.participantId),
  }
  const participantById = participants.reduce<
    Record<number, ResolvedAssignmentParticipant>
  >((acc, participant) => {
    acc[participant.participantId] = participant
    return acc
  }, {})
  const cursorByRole: Record<RoleKey, number> = {
    tank: 0,
    healer: 0,
    melee: 0,
    ranged: 0,
    dps: 0,
    anyone: 0,
  }
  const participantIdsByClass = participants.reduce<Record<number, number[]>>(
    (acc, participant) => {
      if (!participant.classId) return acc
      acc[participant.classId] = acc[participant.classId] ?? []
      acc[participant.classId].push(participant.participantId)
      return acc
    },
    {},
  )
  const cursorByClass: Record<number, number> = {}

  const countAvailableFromIds = (
    ids: number[],
    usedInScope: Set<number>,
    blockedParticipantIds: Set<number>,
  ) =>
    ids.filter(
      (candidateId) =>
        !usedInScope.has(candidateId) &&
        !blockedParticipantIds.has(candidateId),
    ).length

  const pickFromIds = (
    ids: number[],
    getCursor: () => number,
    setCursor: (nextCursor: number) => void,
    usedInScope: Set<number>,
    blockedParticipantIds: Set<number>,
  ): ResolvedAssignmentParticipant | undefined => {
    if (ids.length === 0) return undefined
    for (let offset = 0; offset < ids.length; offset += 1) {
      const candidateIndex = (getCursor() + offset) % ids.length
      const candidateId = ids[candidateIndex]
      if (usedInScope.has(candidateId)) continue
      if (blockedParticipantIds.has(candidateId)) continue
      setCursor((candidateIndex + 1) % ids.length)
      return participantById[candidateId]
    }
    return undefined
  }

  const pickFromRole = (
    role: RoleKey,
    usedInScope: Set<number>,
    blockedParticipantIds: Set<number> = new Set<number>(),
  ): ResolvedAssignmentParticipant | undefined =>
    pickFromIds(
      participantIdsByRole[role],
      () => cursorByRole[role],
      (nextCursor) => {
        cursorByRole[role] = nextCursor
      },
      usedInScope,
      blockedParticipantIds,
    )

  const pickClass = (
    classId: number,
    usedInScope: Set<number>,
    blockedParticipantIds: Set<number> = new Set<number>(),
  ): ResolvedAssignmentParticipant | undefined =>
    pickFromIds(
      participantIdsByClass[classId] ?? [],
      () => cursorByClass[classId] ?? 0,
      (nextCursor) => {
        cursorByClass[classId] = nextCursor
      },
      usedInScope,
      blockedParticipantIds,
    )

  const pick = (
    role: RoleKey,
    usedInScope: Set<number>,
    blockedParticipantIds: Set<number> = new Set<number>(),
  ): ResolvedAssignmentParticipant | undefined => {
    if (role !== 'anyone') {
      return pickFromRole(role, usedInScope, blockedParticipantIds)
    }
    for (const prioritizedRole of rolePriority) {
      const participant = pickFromRole(
        prioritizedRole,
        usedInScope,
        blockedParticipantIds,
      )
      if (participant) return participant
    }
    return pickFromRole('anyone', usedInScope, blockedParticipantIds)
  }

  const availableByRole = (role: Exclude<RoleKey, 'anyone'>): number =>
    participantIdsByRole[role].length
  const availableByClass = (classId: number): number =>
    participantIdsByClass[classId]?.length ?? 0
  const availableAssignableByClass = (
    classId: number,
    usedInScope: Set<number>,
    blockedParticipantIds: Set<number>,
  ) =>
    countAvailableFromIds(
      participantIdsByClass[classId] ?? [],
      usedInScope,
      blockedParticipantIds,
    )
  const availableAssignableByRole = (
    role: RoleKey,
    usedInScope: Set<number>,
    blockedParticipantIds: Set<number>,
  ) =>
    countAvailableFromIds(
      participantIdsByRole[role],
      usedInScope,
      blockedParticipantIds,
    )

  const pickMember = (
    member: Extract<RaidPlanAssignmentEntity, { kind: 'member' }>,
    usedInScope: Set<number>,
    blockedParticipantIds: Set<number> = new Set<number>(),
  ): ResolvedAssignmentParticipant | undefined => {
    const sameCharacterParticipant = participants.find(
      (participant) =>
        participant.characterId === member.character_id &&
        !usedInScope.has(participant.participantId),
    )
    if (sameCharacterParticipant) return sameCharacterParticipant
    if (member.spec_id !== null) {
      const sameSpecParticipant = participants.find(
        (participant) =>
          participant.specId === member.spec_id &&
          !usedInScope.has(participant.participantId) &&
          !blockedParticipantIds.has(participant.participantId),
      )
      if (sameSpecParticipant) return sameSpecParticipant
    }
    const memberRole = asResolverRole(member.role)
    return participants.find(
      (participant) =>
        matchesRole(participant.role, memberRole) &&
        !usedInScope.has(participant.participantId) &&
        !blockedParticipantIds.has(participant.participantId),
    )
  }

  const availableForMember = (
    member: Extract<RaidPlanAssignmentEntity, { kind: 'member' }>,
  ) => {
    const sameCharacterCount = participants.filter(
      (participant) => participant.characterId === member.character_id,
    ).length
    if (sameCharacterCount > 0) return sameCharacterCount
    if (member.spec_id !== null) {
      const sameSpecCount = participants.filter(
        (participant) => participant.specId === member.spec_id,
      )
      if (sameSpecCount.length > 0) return sameSpecCount.length
    }
    const memberRole = asResolverRole(member.role)
    return participants.filter((participant) =>
      matchesRole(participant.role, memberRole),
    ).length
  }

  return {
    pick,
    pickClass,
    pickMember,
    availableForMember,
    availableByRole,
    availableByClass,
    availableAssignableByClass,
    availableAssignableByRole,
  }
}

type DeferredResolverEntity =
  | Extract<RaidPlanAssignmentEntity, { kind: 'by_role' }>
  | Extract<RaidPlanAssignmentEntity, { kind: 'by_class' }>

const resolveAssignmentEntityWithPicker = ({
  entity,
  picker,
  usedParticipantIds,
  blockedParticipantIds,
  spellId,
  scopeId,
  availableAnyoneCount,
  participantByCharacterId,
  override,
  deferAssignableEntity = false,
  onMemberParticipantResolved,
}: {
  entity: RaidPlanAssignmentEntity
  picker: ReturnType<typeof createRoundRobinPicker>
  usedParticipantIds: Set<number>
  blockedParticipantIds?: Set<number>
  spellId: number
  scopeId: string
  availableAnyoneCount: number
  participantByCharacterId?: Record<number, ResolvedAssignmentParticipant>
  override?: EncounterAssignmentEntityOverride
  deferAssignableEntity?: boolean
  onMemberParticipantResolved?: (
    participant: ResolvedAssignmentParticipant,
    memberEntity: Extract<RaidPlanAssignmentEntity, { kind: 'member' }>,
  ) => void
}) => {
  const blocked = blockedParticipantIds ?? new Set<number>()
  if (
    override?.kind === 'participant' &&
    participantByCharacterId &&
    participantByCharacterId[override.characterId]
  ) {
    const participant = participantByCharacterId[override.characterId]
    usedParticipantIds.add(participant.participantId)
    return {
      resolvedEntity: {
        kind: 'participant',
        participant,
        sourceEntity: entity,
        isOverridden: true,
      } as ResolvedAssignmentEntity,
    }
  }
  if (override?.kind === 'aggregate') {
    return {
      resolvedEntity: {
        kind: 'aggregate',
        sourceEntity: override.entity,
        isOverridden: true,
      } as ResolvedAssignmentEntity,
    }
  }
  if (entity.kind === 'member') {
    const participant = picker.pickMember(entity, usedParticipantIds, blocked)
    if (!participant) {
      return {
        resolvedEntity: {
          kind: 'plain',
          sourceEntity: entity,
          issue: {
            type: 'unmatched_member',
            spellId,
            scopeId,
            requested: 1,
            available: picker.availableForMember(entity),
            missing: 1,
            memberName: entity.label,
          },
        } as ResolvedAssignmentEntity,
      }
    }
    usedParticipantIds.add(participant.participantId)
    onMemberParticipantResolved?.(participant, entity)
    return {
      resolvedEntity: {
        kind: 'participant',
        participant,
        sourceEntity: entity,
      } as ResolvedAssignmentEntity,
    }
  }
  if (entity.kind === 'by_role' || entity.kind === 'by_class') {
    if (deferAssignableEntity) {
      return {
        deferredEntity: entity as DeferredResolverEntity,
      }
    }
    if (entity.kind === 'by_role') {
      const role = entity.role === 'anyone' ? 'anyone' : entity.role
      const participant = picker.pick(role, usedParticipantIds, blocked)
      if (!participant) {
        return {
          resolvedEntity: {
            kind: 'plain',
            sourceEntity: entity,
            issue: {
              type: 'insufficient_candidates',
              spellId,
              scopeId,
              role,
              requested: 1,
              available:
                role === 'anyone'
                  ? availableAnyoneCount
                  : picker.availableByRole(role),
              missing: 1,
            },
          } as ResolvedAssignmentEntity,
        }
      }
      usedParticipantIds.add(participant.participantId)
      return {
        resolvedEntity: {
          kind: 'participant',
          participant,
          sourceEntity: entity,
        } as ResolvedAssignmentEntity,
      }
    }
    const participant = picker.pickClass(
      entity.class_id,
      usedParticipantIds,
      blocked,
    )
    if (!participant) {
      return {
        resolvedEntity: {
          kind: 'plain',
          sourceEntity: entity,
          issue: {
            type: 'insufficient_candidates',
            spellId,
            scopeId,
            requested: 1,
            available: picker.availableByClass(entity.class_id),
            missing: 1,
          },
        } as ResolvedAssignmentEntity,
      }
    }
    usedParticipantIds.add(participant.participantId)
    return {
      resolvedEntity: {
        kind: 'participant',
        participant,
        sourceEntity: entity,
      } as ResolvedAssignmentEntity,
    }
  }
  if (
    entity.kind === 'everyone' ||
    entity.kind === 'collection' ||
    entity.kind === 'group' ||
    entity.kind === 'role'
  ) {
    return {
      resolvedEntity: {
        kind: 'aggregate',
        sourceEntity: entity,
      } as ResolvedAssignmentEntity,
    }
  }
  return {
    resolvedEntity: {
      kind: 'plain',
      sourceEntity: entity,
    } as ResolvedAssignmentEntity,
  }
}

export const resolveEventActivityTimelineAssignments = ({
  activityId,
  entries,
  encounterRows,
  abilities,
}: ResolveEventActivityTimelineAssignmentsInput) => {
  const allParticipants = toResolvedParticipants(entries, activityId)
  const resolvedByScopeBoxKey: Record<string, ResolvedAssignmentEntity[]> = {}
  const issues: EventActivityTimelineResolverIssue[] = []

  abilities.forEach((ability, abilityIndex) => {
    const row = encounterRows[abilityIndex]
    if (!row) return
    const assignmentGroups = getEffectiveAssignmentGroups(
      row.assignmentGroups,
      ability.assignment_groups,
    )
    const viewMode: AssignmentViewMode = getEffectiveAssignmentViewMode(
      ability.view_mode,
    )
    const customGrouping = getEffectiveAbilityCustomGrouping(
      ability.spell_id,
      ability.custom_grouping,
    )
    const scopes = getAssignmentScopeIndexes(
      row.entries,
      viewMode,
      customGrouping,
    )
    const remainingExplicitCharacterIds = scopes.reduce<Set<number>>(
      (acc, scope) => {
        const representativeCast = normalizeAssignmentCast(
          ability.casts?.[scope.castIndexes[0]],
          assignmentGroups,
        )
        for (let boxIndex = 0; boxIndex < assignmentGroups; boxIndex += 1) {
          const box = normalizeAssignmentBox(representativeCast.boxes[boxIndex])
          box.entities.forEach((entity) => {
            if (entity.kind === 'member') {
              acc.add(entity.character_id)
            }
          })
        }
        return acc
      },
      new Set<number>(),
    )
    const alternatingUsedParticipantIds = new Set<number>()
    const allSeparateRoundRobinPicker = createRoundRobinPicker(
      allParticipants,
      ['ranged', 'melee', 'healer', 'tank', 'dps'],
    )
    scopes.forEach((scope) => {
      const scopeParticipants =
        viewMode === 'alternating'
          ? allParticipants.filter(
              (participant) =>
                !alternatingUsedParticipantIds.has(participant.participantId),
            )
          : allParticipants
      const blockedParticipantIds = new Set(
        scopeParticipants
          .filter((participant) =>
            remainingExplicitCharacterIds.has(participant.characterId),
          )
          .map((participant) => participant.participantId),
      )
      const representativeCast = normalizeAssignmentCast(
        ability.casts?.[scope.castIndexes[0]],
        assignmentGroups,
      )
      const scopeAssignedParticipantIds = new Set<number>()
      const scopePicker =
        viewMode === 'all_separate'
          ? allSeparateRoundRobinPicker
          : createRoundRobinPicker(scopeParticipants, [
              'ranged',
              'melee',
              'healer',
              'tank',
              'dps',
            ])

      for (let boxIndex = 0; boxIndex < assignmentGroups; boxIndex += 1) {
        const box = normalizeAssignmentBox(representativeCast.boxes[boxIndex])
        const key = `${row.abilityKey}:${scope.id}:${boxIndex}`
        const resolvedEntitiesByIndex: (ResolvedAssignmentEntity | null)[] =
          Array.from({ length: box.entities.length }, () => null)
        const pendingResolverEntities: {
          index: number
          entity:
            | Extract<RaidPlanAssignmentEntity, { kind: 'by_role' }>
            | Extract<RaidPlanAssignmentEntity, { kind: 'by_class' }>
        }[] = []

        box.entities.forEach((entity, index) => {
          const { resolvedEntity, deferredEntity } =
            resolveAssignmentEntityWithPicker({
              entity,
              picker: scopePicker,
              usedParticipantIds: scopeAssignedParticipantIds,
              blockedParticipantIds,
              spellId: ability.spell_id,
              scopeId: scope.id,
              availableAnyoneCount: scopeParticipants.length,
              deferAssignableEntity: true,
              onMemberParticipantResolved: (participant, memberEntity) => {
                if (participant.characterId === memberEntity.character_id) {
                  remainingExplicitCharacterIds.delete(
                    memberEntity.character_id,
                  )
                }
              },
            })
          if (deferredEntity) {
            pendingResolverEntities.push({ index, entity: deferredEntity })
            return
          }
          if (!resolvedEntity) return
          if (resolvedEntity.issue) {
            issues.push(resolvedEntity.issue)
          }
          resolvedEntitiesByIndex[index] = resolvedEntity
        })

        while (pendingResolverEntities.length > 0) {
          const rankedEntities = pendingResolverEntities
            .map((entry) => {
              if (entry.entity.kind === 'by_role') {
                const role =
                  entry.entity.role === 'anyone' ? 'anyone' : entry.entity.role
                return {
                  ...entry,
                  available: scopePicker.availableAssignableByRole(
                    role,
                    scopeAssignedParticipantIds,
                    blockedParticipantIds,
                  ),
                }
              }
              return {
                ...entry,
                available: scopePicker.availableAssignableByClass(
                  entry.entity.class_id,
                  scopeAssignedParticipantIds,
                  blockedParticipantIds,
                ),
              }
            })
            .sort((left, right) => {
              if (left.available !== right.available) {
                return left.available - right.available
              }
              return left.index - right.index
            })
          const nextEntry = rankedEntities[0]
          if (!nextEntry) break
          const pendingIndex = pendingResolverEntities.findIndex(
            (entry) => entry.index === nextEntry.index,
          )
          if (pendingIndex >= 0) {
            pendingResolverEntities.splice(pendingIndex, 1)
          }
          const { resolvedEntity } = resolveAssignmentEntityWithPicker({
            entity: nextEntry.entity,
            picker: scopePicker,
            usedParticipantIds: scopeAssignedParticipantIds,
            blockedParticipantIds,
            spellId: ability.spell_id,
            scopeId: scope.id,
            availableAnyoneCount: scopeParticipants.length,
          })
          if (!resolvedEntity) continue
          if (resolvedEntity.issue) {
            issues.push(resolvedEntity.issue)
          }
          resolvedEntitiesByIndex[nextEntry.index] = resolvedEntity
        }

        resolvedByScopeBoxKey[key] = resolvedEntitiesByIndex.map(
          (resolvedEntity, index) =>
            resolvedEntity ?? {
              kind: 'plain',
              sourceEntity: box.entities[index],
            },
        )
      }
      if (viewMode === 'alternating') {
        scopeAssignedParticipantIds.forEach((participantId) =>
          alternatingUsedParticipantIds.add(participantId),
        )
      }
    })
  })

  return {
    resolvedByScopeBoxKey,
    issues,
  }
}

type ResolveEventActivityCastTargetsInput = {
  activityId: number
  entries: EventActivityParticipantEntry[]
  actors: RaidPlanActor[]
  generalCasts?: RaidPlanCast[]
  castTargetOverrides?: EventPlanCastTargetOverrides
}

type ResolveEventActivityCastTargetsResult = {
  resolvedByCastId: Record<string, ResolvedAssignmentEntity[]>
  issues: EventActivityTimelineResolverIssue[]
}

export const resolveEventActivityCastTargets = ({
  activityId,
  entries,
  actors,
  generalCasts,
  castTargetOverrides,
}: ResolveEventActivityCastTargetsInput): ResolveEventActivityCastTargetsResult => {
  const participants = toResolvedParticipants(entries, activityId)
  const participantByCharacterId = participants.reduce<
    Record<number, ResolvedAssignmentParticipant>
  >((acc, participant) => {
    acc[participant.characterId] = participant
    return acc
  }, {})
  const allCasts = [
    ...actors.flatMap((actor) => actor.casts),
    ...(generalCasts ?? []),
  ]
  const resolvedByCastId: Record<string, ResolvedAssignmentEntity[]> = {}
  const issues: EventActivityTimelineResolverIssue[] = []
  allCasts.forEach((cast) => {
    const targets = cast.targets ?? []
    if (!targets.length) return
    const picker = createRoundRobinPicker(participants, [
      'ranged',
      'melee',
      'healer',
      'tank',
      'dps',
    ])
    const usedParticipantIds = new Set<number>()
    const resolvedEntities = targets.flatMap((group, groupIndex) =>
      group.map((entity, entityIndex) => {
        const entityId = `${groupIndex}:${entityIndex}`
        const override = getCastTargetEntityOverride({
          castTargetOverrides,
          castId: cast.id,
          entityId,
        })
        const { resolvedEntity } = resolveAssignmentEntityWithPicker({
          entity,
          picker,
          usedParticipantIds,
          spellId: cast.spell_id ?? 0,
          scopeId: cast.id,
          availableAnyoneCount: participants.length,
          participantByCharacterId,
          override,
        })
        if (!resolvedEntity) {
          return {
            kind: 'plain',
            sourceEntity: entity,
          } as ResolvedAssignmentEntity
        }
        if (resolvedEntity.issue) {
          issues.push(resolvedEntity.issue)
        }
        return resolvedEntity
      }),
    )
    resolvedByCastId[cast.id] = resolvedEntities
  })
  return {
    resolvedByCastId,
    issues,
  }
}
